import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '@app/shared/utils/icon/icon.component';
import { IconName } from '@app/shared/utils/icon/icons';
import { PdfPreviewService } from '@app/core/services/pdf-preview.service';
import { Size } from '@app/core/services/utils/a4-ratio-helper';
import { TranslatePipe } from '@app/shared/pipes';
import { Constants } from '@app/shared/utils';
import { Logger } from '@app/core/services/utils/logger';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = Constants.PDF_WORKER_SCRIPT;

@Component({
  selector: 'app-pdf-preview',
  templateUrl: './pdf-preview.component.html',
  styleUrls: ['./pdf-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, CommonModule, FormsModule, NgClass, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PdfPreviewComponent implements OnInit {
  @Input() src: string;
  @Input() size: Size;
  @Input() showAllPages: boolean = false; // Show single or all pages altogether.

  @ViewChild('pdfContainer', { static: true }) pdfContainer!: ElementRef<HTMLDivElement>;

  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowLeft':
        this.prevPage();
        break;
      case 'ArrowRight':
        this.nextPage();
        break;
      case '+':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '=':
        this.resetZoom();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.fitToContainer();
  }

  private pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;

  private initialScale: number = 1; // the scale that fits container on load
  public zoom: number = 1; // user zoom relative to initialScale
  public readonly zoomMin: number = 1;
  public readonly zoomMax: number = 3;
  private readonly zoomStep: number = 0.25;

  public currentPage: number = 1;
  public totalPages: number = 0;

  public IconName = IconName;

  constructor(
    private readonly pdfPreviewService: PdfPreviewService,
    private readonly logger: Logger,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    if (this.src) this.loadPdf();
  }

  public getMaxWidth(): string {
    return this.pdfPreviewService.getPdfContainerMaxWidth() + 'px';
  }

  public getMaxHeight(): string {
    return this.pdfPreviewService.getPdfContainerMaxHeight() + 'px';
  }

  public close(): void {
    this.pdfPreviewService.close();
  }

  public zoomChanged(zoom: number) {
    this.zoom = zoom;
    this.renderPdf();
  }

  public zoomIn() {
    this.zoom = Math.min(this.zoom + this.zoomStep, this.zoomMax);
    this.renderPdf();
  }

  public zoomOut() {
    this.zoom = Math.max(this.zoom - this.zoomStep, this.zoomMin);
    this.renderPdf();
  }

  public resetZoom() {
    this.zoom = 1.0;
    this.renderPdf();
  }

  public prevPage() {
    if (!this.pdfDoc || this.currentPage <= 1) return;
    this.currentPage--;
    this.renderPdf();
  }

  public nextPage() {
    if (!this.pdfDoc || this.currentPage >= this.totalPages) return;
    this.currentPage++;
    this.renderPdf();
  }

  private async loadPdf() {
    try {
      this.pdfDoc = await pdfjsLib.getDocument(this.src).promise;
      this.onLoadComplete();
    } catch (err) {
      this.onError(err);
    }
  }

  private renderPdf() {
    const container = this.pdfContainer.nativeElement;
    container.innerHTML = ''; // clear existing

    if (this.showAllPages) this.renderAllPages();
    else this.renderPage(this.currentPage);
  }

  private async renderAllPages() {
    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      await this.renderPage(pageNum);
    }
  }

  private async renderPage(pageNum: number) {
    const page = await this.pdfDoc.getPage(pageNum);
    const scale = this.initialScale * this.zoom; // final scale for rendering
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    const renderContext = {
      canvasContext: canvas.getContext('2d'),
      viewport,
    };

    await page.render(renderContext).promise;
    this.pdfContainer.nativeElement.appendChild(canvas);
    this.cdr.detectChanges();
  }

  private async fitToContainer() {
    if (!this.pdfDoc) return;

    const container = this.pdfContainer.nativeElement;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const page = await this.pdfDoc.getPage(1);
    const unscaledViewport = page.getViewport({ scale: 1 });

    // fit to container: choose the smaller of width/height scale
    const scaleW = containerWidth / unscaledViewport.width;
    const scaleH = containerHeight / unscaledViewport.height;
    const scale = Math.min(scaleW, scaleH);

    this.initialScale = scale;
    this.renderPdf();
  }

  private onLoadComplete(): void {
    // Get total page count:
    this.totalPages = this.pdfDoc.numPages;
    this.currentPage = 1;
    this.fitToContainer();
    this.pdfPreviewService.showLoader(false);

    // Used for debugging only. Helps determine the size of the PDF.
    // this.pdfDoc.getPage(1).then((page: pdfjsLib.PDFPageProxy) => {
    //   const viewport = page.getViewport({ scale: 1 });

    //   /**
    //    * viewport.width and viewport.height gives us the dimensions in pixels at scale = 1.
    //    * scale: 1 gives us the natural size of the PDF. If we're using a zoom, we can multiply the width and height accordingly.
    //    */
    //   console.log('PDF size:', viewport.width, viewport.height);
    // });
  }

  private onError(error: Error): void {
    this.pdfPreviewService.showLoader(true);
    this.logger.error(error);
  }
}
